import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { Card } from "../../../components";
import { useTopikMotion } from "./topikMotion";
import ScrollCue from "./ScrollCue";

// The report mixes what the facilitator gets to see (the final picks, the observation, its
// seriousness and the goal - exactly what the GM monitor and dashboard report carry) with what
// stays the player's own. Each shared block is marked with this tag so the split is visible
// on the page rather than left for the player to guess.
const SharedBadge = ({ label }) => (
  <span className="topik-complete__shared-badge">
    <VisibilityIcon className="topik-complete__shared-icon" aria-hidden="true" />
    {label}
  </span>
);

SharedBadge.propTypes = {
  label: PropTypes.string.isRequired,
};

// A titled block inside the report. `accent` tints its marker dot to echo the game's two-sided
// structure: negative (vermillion) for the harmful analysis, positive (blue) for the helpful,
// neutral for the framing and commitment blocks. Colour is never the sole signal - each block
// still carries its own label. `sharedLabel` marks a block the facilitator sees.
const Section = ({ label, accent, sharedLabel, children, variants }) => (
  <motion.div
    className={`topik-complete__section topik-complete__section--${accent}`}
    variants={variants}
  >
    <div className="topik-complete__section-title">
      {label}
      {sharedLabel && <SharedBadge label={sharedLabel} />}
    </div>
    <div className="topik-complete__section-value">{children}</div>
  </motion.div>
);

Section.propTypes = {
  label: PropTypes.string.isRequired,
  accent: PropTypes.oneOf(["neutral", "negative", "positive"]),
  sharedLabel: PropTypes.string,
  children: PropTypes.node.isRequired,
  variants: PropTypes.object,
};

Section.defaultProps = {
  accent: "neutral",
  sharedLabel: null,
  variants: undefined,
};

// Seriousness as a five-pip meter rather than a bare "4 / 5" - a keepsake-report flourish. The
// spoken value stays available to screen readers (and to print) via the visually-hidden label.
const SeriousnessMeter = ({ value, max, srLabel }) => (
  <div className="topik-complete__meter" role="img" aria-label={srLabel}>
    {Array.from({ length: max }, (_, i) => (
      <span
        key={i}
        className={`topik-complete__pip ${
          i < value ? "topik-complete__pip--on" : ""
        }`}
      />
    ))}
    <span className="topik-complete__sr-only">{srLabel}</span>
  </div>
);

SeriousnessMeter.propTypes = {
  value: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  srLabel: PropTypes.string.isRequired,
};

const YesNo = ({ value, label }) => (
  <span
    className={`topik-complete__yesno topik-complete__yesno--${value ? "yes" : "no"}`}
  >
    {value ? (
      <CheckCircleIcon className="topik-complete__yesno-icon" />
    ) : (
      <RemoveCircleOutlineIcon className="topik-complete__yesno-icon" />
    )}
    {label}
  </span>
);

YesNo.propTypes = {
  value: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
};

const TopikCompleteView = ({ t, session, gameSubject }) => {
  // Completion ceremony (§1.2-7 / A7): the two cards settle side-by-side first, then the
  // sections fade in sequence - one gentle pass, no confetti. Reduced-motion collapses it all
  // to an instant reveal.
  const { reduce, calm } = useTopikMotion();
  const container = {
    initial: {},
    animate: { transition: reduce ? {} : { staggerChildren: 0.08, delayChildren: 0.35 } },
  };
  const item = {
    initial: reduce ? { opacity: 1 } : { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
  };
  const {
    chosenNegativeCard, chosenPositiveCard, observation, seriousness, issues,
    risk, riskMitigation, solution, solutionMaximizer, goal, goalCapability, goalWill,
    shortCircuited,
  } = session;

  const yesNo = (value) => t(`topik-complete-view.${value ? "yes" : "no"}`);
  const shared = t("topik-complete-view.shared-badge");

  // §6.2a(d) player-own-run takeaway. No client-side PDF pipeline exists and the backend
  // container has no headless-Chrome runtime for grover, so this uses the browser's native
  // print-to-PDF over a print stylesheet that isolates this report (a server-rendered PDF is
  // deferred - see the wave-3 status note).
  const handleDownload = () => window.print();

  // Which report groups actually have content - lets each group's frame collapse cleanly for a
  // short-circuited run (observation-only) instead of leaving empty tinted panels behind.
  const hasHarmful = (issues && issues.length > 0) || risk || riskMitigation;
  const hasHelpful = solution || solutionMaximizer;
  const hasCommitment = Boolean(goal);

  return (
    <>
      <Helmet>
        <meta name="theme-color" content="#222222" />
        <html className="card-background" />
        {/* Scopes the topik print stylesheet to this view so printing other routes is unaffected. */}
        <body className="topik-print-page" />
      </Helmet>
      <motion.main
        className="topik-view topik-complete-view topik-print-root"
        variants={container}
        initial="initial"
        animate="animate"
      >
        {gameSubject && (
          <motion.p className="topik-play-shell__subject" variants={item}>{gameSubject}</motion.p>
        )}
        <motion.div className="topik-complete__kicker" variants={item}>
          {t("topik-complete-view.negative-label").replace(/:$/, "")}
          <span className="topik-complete__kicker-dot" />
          {t("topik-complete-view.positive-label").replace(/:$/, "")}
        </motion.div>
        <motion.h1 className="topik-view__title" variants={item}>
          {t("topik-complete-view.title")}
        </motion.h1>
        <motion.p className="topik-view__instruction topik-complete__instruction" variants={item}>
          {shortCircuited
            ? t("topik-complete-view.short-circuit-instruction")
            : t("topik-complete-view.instruction")}
        </motion.p>
        <motion.p className="topik-complete__shared-legend" variants={item}>
          <SharedBadge label={shared} />
          {t("topik-complete-view.shared-legend")}
        </motion.p>

        <motion.div className="topik-complete__pair-heading" variants={item}>
          {t("topik-complete-view.pair-label")}
          <SharedBadge label={shared} />
        </motion.div>
        <motion.div
          className="topik-complete__pair"
          variants={item}
          transition={calm}
        >
          <div className="topik-complete__pair-card">
            <span className="topik-complete__pair-label topik-complete__pair-label--negative">
              {t("topik-complete-view.negative-label").replace(/:$/, "")}
            </span>
            <Card
              cardId={`ctx-neg-${chosenNegativeCard.id}`}
              title={chosenNegativeCard.title}
              description={chosenNegativeCard.description || ""}
              suit="negative"
              exposed
            />
          </div>
          <div className="topik-complete__pair-card">
            <span className="topik-complete__pair-label topik-complete__pair-label--positive">
              {t("topik-complete-view.positive-label").replace(/:$/, "")}
            </span>
            <Card
              cardId={`ctx-pos-${chosenPositiveCard.id}`}
              title={chosenPositiveCard.title}
              description={chosenPositiveCard.description || ""}
              suit="positive"
              exposed
            />
          </div>
        </motion.div>

        {observation && (
          <motion.figure className="topik-complete__lead" variants={item}>
            <figcaption className="topik-complete__lead-label">
              {t("topik-complete-view.observation-label")}
              <SharedBadge label={shared} />
            </figcaption>
            <blockquote className="topik-complete__lead-quote">{observation}</blockquote>
          </motion.figure>
        )}

        <div className="topik-complete__report">
          {seriousness != null && (
            <Section label={t("topik-complete-view.seriousness-label")} sharedLabel={shared} variants={item}>
              <SeriousnessMeter
                value={seriousness}
                max={5}
                srLabel={t("topik-complete-view.seriousness-value", { seriousness })}
              />
            </Section>
          )}

          {hasHarmful && (
            <motion.div className="topik-complete__group topik-complete__group--negative" variants={item}>
              {issues && issues.length > 0 && (
                <Section label={t("topik-complete-view.issues-label")} accent="negative">
                  <ul className="topik-complete__issue-list">
                    {issues.map((issue) => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </ul>
                </Section>
              )}
              {risk && (
                <Section label={t("topik-complete-view.risk-label")} accent="negative">
                  {risk}
                </Section>
              )}
              {riskMitigation && (
                <Section label={t("topik-complete-view.risk-mitigation-label")} accent="negative">
                  {riskMitigation}
                </Section>
              )}
            </motion.div>
          )}

          {hasHelpful && (
            <motion.div className="topik-complete__group topik-complete__group--positive" variants={item}>
              {solution && (
                <Section label={t("topik-complete-view.solution-label")} accent="positive">
                  {solution}
                </Section>
              )}
              {solutionMaximizer && (
                <Section label={t("topik-complete-view.solution-maximizer-label")} accent="positive">
                  {solutionMaximizer}
                </Section>
              )}
            </motion.div>
          )}

          {hasCommitment && (
            <motion.div className="topik-complete__group topik-complete__group--goal" variants={item}>
              <Section label={t("topik-complete-view.goal-label")} sharedLabel={shared}>{goal}</Section>
              <div className="topik-complete__gates">
                <Section label={t("topik-complete-view.capability-label")}>
                  <YesNo value={Boolean(goalCapability)} label={yesNo(goalCapability)} />
                </Section>
                <Section label={t("topik-complete-view.will-label")}>
                  <YesNo value={Boolean(goalWill)} label={yesNo(goalWill)} />
                </Section>
              </div>
            </motion.div>
          )}
        </div>

        <motion.div className="topik-complete__download-wrap topik-no-print" variants={item}>
          <button
            type="button"
            className="topik-view__submit-button topik-complete__download"
            onClick={handleDownload}
          >
            <DownloadIcon className="topik-complete__download-icon" />
            {t("topik-complete-view.download")}
          </button>
        </motion.div>
        <ScrollCue />
      </motion.main>
    </>
  );
};

TopikCompleteView.propTypes = {
  t: PropTypes.func.isRequired,
  gameSubject: PropTypes.string,
  session: PropTypes.shape({
    chosenNegativeCard: PropTypes.object.isRequired,
    chosenPositiveCard: PropTypes.object.isRequired,
    observation: PropTypes.string,
    seriousness: PropTypes.number,
    issues: PropTypes.arrayOf(PropTypes.string),
    risk: PropTypes.string,
    riskMitigation: PropTypes.string,
    solution: PropTypes.string,
    solutionMaximizer: PropTypes.string,
    goal: PropTypes.string,
    goalCapability: PropTypes.bool,
    goalWill: PropTypes.bool,
    shortCircuited: PropTypes.bool,
  }).isRequired,
};

TopikCompleteView.defaultProps = {
  gameSubject: null,
};

const mapStateToProps = (state) => ({
  session: state.topik.session,
  gameSubject: state.game.game ? state.game.game.subject : null,
});

export default compose(withTranslation("views"), connect(mapStateToProps))(TopikCompleteView);
